/**
 * Reading an `OrderGateInput` out of the stores.
 *
 * `validateOrder` takes seventeen fields and every one of them is a fact about
 * the account, the market or the book. These are the readers for the ones that
 * come out of untyped projection rows — positions, orders, the ticker, the
 * session — and they live here rather than inside `<OrderTicket>` because the
 * pro ladder asks the same question of the same rows and a second copy would be
 * two opinions about whether an account can afford an order.
 *
 * Every one is defensive: there is no `Order` or `Position` interface on this
 * side of the wire, so a missing or misspelled field reads as zero rather than
 * as `NaN` propagating into a size.
 *
 * No React, no DOM, no clock (invariant 10). `bigint` micro-units throughout;
 * no float touches a size or a balance (invariant 6).
 */

import { parseFixed, SHARES_DP, USDC_DP } from '@/lib/decimal'
import type { Outcome } from '@/lib/decimal'
import type { PendingOrder } from '@/stores/pending-orders'
import type { TradingLimits } from './order-config'
import { RESTING_STATUSES } from './pro-ladder'
import { floorToStep } from './order-validation'

/**
 * How many shares of one outcome this account holds in this market.
 *
 * A position carries **both** `up_shares` and `down_shares` — outcome is a
 * field inside it, not part of its identity — so both are read off one row.
 */
export function positionSharesOf(
  positions: readonly Record<string, unknown>[],
  marketId: string | null,
  outcome: Outcome,
): bigint {
  if (!marketId) return 0n
  const row = positions.find((p) => p.market_id === marketId)
  return microOf(row?.[outcome === 'UP' ? 'up_shares' : 'down_shares'], SHARES_DP)
}

/**
 * Shares of `outcome` this account's own live sells have already promised.
 *
 * **This is not the ladder's normalisation and must not be confused with it.**
 * `ownBookFrom` folds a `BUY DOWN` onto the ask column, because on the book it
 * is the same rung. Here it must *not* count: a `BUY DOWN` mints a fresh pair
 * against cash and delivers no UP shares at all, so counting it would make the
 * account look less able to cover than it is and push a genuinely covered sell
 * into being minted. Only a literal `SELL` of this outcome reserves a share.
 *
 * This subtraction is `matcher.coveredSplit`'s `reservedClose` on the client's
 * side of the wire. A pending order counts too — it is a promise this tab has
 * already made, and it is exactly the beat the projection is behind.
 */
export function restingSellShares(
  orders: readonly Record<string, unknown>[],
  pending: readonly PendingOrder[],
  marketId: string | null,
  outcome: Outcome,
): bigint {
  if (!marketId) return 0n
  let total = 0n
  for (const order of orders) {
    if (order.market_id !== marketId) continue
    if (order.side !== 'SELL' || order.outcome !== outcome) continue
    if (!RESTING_STATUSES.has(String(order.status))) continue
    total += microOf(order.remaining_size ?? '0', SHARES_DP)
  }
  for (const row of pending) {
    if (row.marketId !== marketId) continue
    if (row.side !== 'SELL' || row.outcome !== outcome) continue
    total += microOf(row.size, SHARES_DP)
  }
  return total
}

/** How many orders this account has resting, in total and in one market. */
export function countOpen(
  orders: readonly Record<string, unknown>[],
  marketId: string | null,
): { account: number; market: number } {
  let account = 0
  let market = 0
  for (const order of orders) {
    if (!RESTING_STATUSES.has(String(order.status))) continue
    account += 1
    if (marketId && order.market_id === marketId) market += 1
  }
  return { account, market }
}

/** `open_interest` off the ticker, or `null` where the venue has not published one. */
export function openInterestOf(ticker: unknown): bigint | null {
  const value = (ticker as Record<string, unknown> | null)?.open_interest
  if (typeof value !== 'string') return null
  try {
    return parseFixed(value, SHARES_DP)
  } catch {
    return null
  }
}

/**
 * `balances.available` in micro-USDC.
 *
 * The four components are rendered separately elsewhere (invariant 9); order
 * entry needs exactly one of them, and it is `available` — not `equity`. Money
 * already reserved by a resting order is not spendable twice, which is why
 * `order_locked` is subtracted from the wallet balance rather than added back
 * into it, even though nothing on-chain actually holds it (§F26l).
 */
export function availableMicroUsdcOf(balance: unknown): bigint | null {
  const balances = (balance as { balances?: Record<string, unknown> } | null)?.balances
  return microOrNull(balances?.available, USDC_DP)
}

export function accountStatusOf(session: unknown): string | null {
  const status = (session as { account_status?: unknown } | null)?.account_status
  return typeof status === 'string' ? status : null
}

export function screeningBlocked(session: unknown): boolean {
  const screening = (session as { screening_status?: unknown } | null)?.screening_status
  return typeof screening === 'string' && screening !== 'CLEAR'
}

/**
 * The largest size this balance can buy at a price, on the step and under the cap.
 *
 * `available_µ × 100 / cents` is micro-shares. Integer division floors, which is
 * the direction that never proposes a size the balance cannot cover.
 */
export function affordableShares(
  availableMicroUsdc: bigint | null,
  priceCents: number | null,
  limits: TradingLimits,
): bigint {
  if (availableMicroUsdc === null || priceCents === null || priceCents <= 0) return 0n
  const shares = (availableMicroUsdc * 100n) / BigInt(priceCents)
  const capped = shares > limits.maxOrderShares ? limits.maxOrderShares : shares
  return floorToStep(capped, limits.sizeStep)
}

/**
 * A typed size as micro-shares.
 *
 * Refuses anything that is not a plain decimal within `SHARES_DP`, so a paste
 * of `1e6` or `1,000` is zero rather than a number the user did not type.
 */
export function microSharesOf(text: string): bigint {
  const trimmed = text.trim()
  if (!/^\d*(?:\.\d{0,6})?$/.test(trimmed) || trimmed === '' || trimmed === '.') return 0n
  try {
    return parseFixed(trimmed, SHARES_DP)
  } catch {
    return 0n
  }
}

function microOf(value: unknown, dp: number): bigint {
  return microOrNull(value, dp) ?? 0n
}

function microOrNull(value: unknown, dp: number): bigint | null {
  if (typeof value !== 'string') return null
  try {
    return parseFixed(value, dp)
  } catch {
    return null
  }
}
